import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Check, X, PencilSimple, WarningCircle, Image } from "@phosphor-icons/react";
import { api, apiError } from "../../lib/api";
import { fmtTime, inputCls, outOfRange, rangeText } from "./shared";

function CompletedValue({ item }) {
  if (item.proof_type === "boolean" || item.proof_type === "photo") {
    const pass = item.result === "pass";
    return <span className={`text-xs font-bold px-2.5 py-1 rounded-md border ${pass ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{item.proof_type === "photo" ? "Photo logged" : pass ? "PASS" : "FAIL"}</span>;
  }
  if (item.proof_type === "numeric") {
    const pass = item.result === "pass";
    return <span className={`font-heading font-bold text-lg ${pass ? "text-emerald-700" : "text-red-600"}`}>{item.value}{item.unit ? ` ${item.unit}` : ""}</span>;
  }
  return <span className="text-sm text-slate-700">{item.value}</span>;
}

export function ItemRow({ runId, item, tz, locked, onLogged }) {
  const pending = item.result === "pending";
  const [editing, setEditing] = useState(pending);
  const [value, setValue] = useState(item.proof_type === "numeric" && item.value != null ? String(item.value) : item.proof_type === "boolean" ? item.value : "");
  const [comment, setComment] = useState(item.comment || "");
  const [photo, setPhoto] = useState(null);
  const [showNote, setShowNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);
  const fileRef = useRef(null);

  const isNumeric = item.proof_type === "numeric";
  const isBool = item.proof_type === "boolean";
  const isPhoto = item.proof_type === "photo";
  const isText = item.proof_type === "text";
  const failing = (isBool && value === false) || (isNumeric && outOfRange(item, value));
  const needsComment = failing || item.comment_required;
  const needsPhoto = (isPhoto || item.photo_required) && !photo && !item.photo_path;
  const ready = !needsPhoto && (isText ? comment.trim().length >= 2 : isBool ? typeof value === "boolean" : isNumeric ? value !== "" && !Number.isNaN(Number(value)) : true)
    && (!needsComment || comment.trim().length >= 3);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { value: isNumeric ? Number(value) : isBool ? value : isText ? comment : true, comment, photo_base64: photo };
      const { data } = await api.post(`/checklists/runs/${runId}/items/${item.id}`, body);
      onLogged(data);
      setEditing(false);
      setPhoto(null);
      toast.success(data.item.result === "fail" ? "Logged as exception — managers alerted" : "Line logged");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const openPhoto = async () => {
    try {
      const { data } = await api.get(`/checklists/runs/${runId}/items/${item.id}/photo`);
      setViewPhoto(data.photo_base64);
    } catch (e) { toast.error(apiError(e)); }
  };

  const tone = pending ? "border-slate-200" : item.result === "fail" ? "border-red-300 bg-red-50/40" : "border-emerald-200 bg-emerald-50/30";

  return (
    <div data-testid={`item-row-${item.id}`} className={`border rounded-xl p-4 sm:p-5 ${tone}`}>
      <div className="flex items-start gap-3">
        <span className={`h-7 w-7 shrink-0 rounded-md flex items-center justify-center text-xs font-bold ${pending ? "bg-slate-100 text-slate-500" : item.result === "fail" ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}>
          {pending ? item.order : item.result === "fail" ? <WarningCircle size={16} weight="bold" /> : <Check size={16} weight="bold" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-slate-900">{item.title}</p>
            {item.critical && <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded">Critical</span>}
            {item.photo_required && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-200 px-2 py-0.5 rounded flex items-center gap-1"><Camera size={12} /> Photo</span>}
          </div>
          {item.description && <p className="text-sm text-slate-500 mt-0.5">{item.description}</p>}
          {isNumeric && <p className="text-xs font-semibold text-slate-400 mt-1">{rangeText(item)}</p>}

          {!editing && !pending && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <CompletedValue item={item} />
              {(item.photo_path || item.proof_type === "photo") && (
                <button data-testid={`item-view-photo-${item.id}`} onClick={openPhoto} className="text-xs font-bold text-slate-600 flex items-center gap-1 border border-slate-300 rounded-md px-2.5 py-1 bg-white hover:border-slate-900"><Image size={14} /> View photo</button>
              )}
              <span className="text-xs text-slate-400">by {item.completed_by} · {fmtTime(item.completed_at, tz)}{item.revised ? " · revised" : ""}</span>
            </div>
          )}
          {!editing && item.comment && !isText && (
            <p data-testid={`item-comment-view-${item.id}`} className="mt-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2">{item.comment}</p>
          )}

          {editing && !locked && (
            <div className="mt-3 space-y-3">
              {isBool && (
                <div className="flex gap-2">
                  <button data-testid={`item-pass-${item.id}`} onClick={() => setValue(true)} className={`h-12 flex-1 rounded-lg text-sm font-bold border tap-press ${value === true ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-600"}`}>Pass</button>
                  <button data-testid={`item-fail-${item.id}`} onClick={() => setValue(false)} className={`h-12 flex-1 rounded-lg text-sm font-bold border tap-press ${value === false ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-700 border-slate-300 hover:border-red-600"}`}>Fail</button>
                </div>
              )}
              {isNumeric && (
                <div className="flex items-center gap-2">
                  <input data-testid={`item-value-${item.id}`} type="number" step="0.1" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)}
                    placeholder="Reading" className={`h-12 w-40 px-3 rounded-lg border text-lg font-heading font-bold bg-white focus:outline-none ${failing ? "border-red-500 text-red-700" : "border-slate-300 focus:border-slate-900"}`} />
                  {item.unit && <span className="text-sm font-bold text-slate-500">{item.unit}</span>}
                  {failing && <span data-testid={`item-range-warning-${item.id}`} className="text-xs font-bold text-red-600 flex items-center gap-1"><WarningCircle size={14} weight="fill" /> Outside safe range</span>}
                </div>
              )}
              {(isPhoto || item.photo_required) && (
                photo ? (
                  <div className="relative inline-block">
                    <img src={photo} alt="proof" className="h-28 w-40 object-cover rounded-lg border border-slate-200" data-testid={`item-photo-preview-${item.id}`} />
                    <button onClick={() => setPhoto(null)} className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-1"><X size={12} /></button>
                  </div>
                ) : (
                  <button data-testid={`item-photo-${item.id}`} onClick={() => fileRef.current?.click()}
                    className="h-12 px-4 rounded-lg border border-dashed border-slate-400 text-sm font-bold text-slate-600 flex items-center gap-2 bg-white hover:border-slate-900 tap-press">
                    <Camera size={18} /> {item.photo_path ? "Replace photo" : "Capture photo"}
                  </button>
                )
              )}
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid={`item-photo-input-${item.id}`} />
              {(isText || needsComment || showNote) ? (
                <textarea data-testid={`item-comment-${item.id}`} value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                  placeholder={isText ? "Enter the log entry" : failing ? "Corrective action / reason (required)" : "Note"}
                  className={`${inputCls} h-auto py-2 ${failing && comment.trim().length < 3 ? "border-red-400" : ""}`} />
              ) : (
                <button onClick={() => setShowNote(true)} className="text-xs font-bold text-slate-500 hover:text-slate-900">+ Add note</button>
              )}
              <div className="flex items-center gap-2">
                <button data-testid={`item-save-${item.id}`} onClick={save} disabled={!ready || busy}
                  className="h-11 px-5 rounded-lg bg-slate-900 text-white text-sm font-bold disabled:opacity-40 tap-press hover:bg-slate-800">{busy ? "Saving…" : "Log line"}</button>
                {!pending && <button onClick={() => setEditing(false)} className="h-11 px-4 rounded-lg border border-slate-300 text-sm font-bold text-slate-600 bg-white">Cancel</button>}
              </div>
            </div>
          )}
        </div>
        {!editing && !locked && (
          <button data-testid={`item-edit-${item.id}`} onClick={() => setEditing(true)} title="Edit entry" className="p-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200"><PencilSimple size={18} /></button>
        )}
      </div>
      {viewPhoto && (
        <div className="fixed inset-0 z-[1100] bg-slate-950/70 flex items-center justify-center p-4" onClick={() => setViewPhoto(null)}>
          <img src={viewPhoto} alt="proof" data-testid={`item-photo-full-${item.id}`} className="max-h-[85vh] max-w-full rounded-xl" />
        </div>
      )}
    </div>
  );
}
